import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

export interface Step {
  title: string;
  description?: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepIndex: number) => void;
  className?: string;
}

export function Stepper({ steps, currentStep, onStepClick, className }: StepperProps) {
  return (
    <div className={cn("w-full", className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          const isClickable = onStepClick && index < currentStep;

          return (
            <div key={index} className="flex items-center flex-1">
              {/* Step indicator */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => isClickable && onStepClick(index)}
                  disabled={!isClickable}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all",
                    isCompleted && "bg-green-600 text-white",
                    isCurrent && "bg-blue-600 text-white ring-4 ring-blue-200",
                    !isCompleted && !isCurrent && "bg-gray-200 text-gray-600",
                    isClickable && "cursor-pointer hover:ring-2 hover:ring-blue-300",
                    !isClickable && "cursor-default"
                  )}
                >
                  {isCompleted ? (
                    <Check className="h-5 w-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>

                {/* Step label (desktop only) */}
                <div className="mt-2 text-center hidden md:block max-w-[120px]">
                  <p className={cn(
                    "text-xs font-medium truncate",
                    (isCompleted || isCurrent) ? "text-gray-900" : "text-gray-500"
                  )}>
                    {step.title}
                  </p>
                  {step.description && (
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">
                      {step.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className={cn(
                  "flex-1 h-1 mx-2 rounded transition-all",
                  index < currentStep ? "bg-green-600" : "bg-gray-200"
                )} />
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: Show current step title below */}
      <div className="mt-4 md:hidden text-center">
        <p className="text-sm font-medium">{steps[currentStep]?.title}</p>
        {steps[currentStep]?.description && (
          <p className="text-xs text-gray-500">{steps[currentStep]?.description}</p>
        )}
      </div>
    </div>
  );
}
